<template>
    <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
            <div class="card-icon icon-style">
                <md-icon>payments</md-icon>
                {{ title }}
            </div>
        </md-card-header>
        <div class="has-invoice" v-if="hasInvoice">
            <md-icon class="invoice-icon-color">task</md-icon>
            <i>*Costs cannot be changed because trip has been invoiced</i>
        </div>
        <div class="is-approved" v-if="isCostsApproved">
            <md-icon class="invoice-icon-color">task</md-icon>
            <i>*Costs cannot be changed because trip has been approved</i>
        </div>
        <md-card-content>
            <div v-if="updatingRates">
                <loading-component-spinner loading-text="Rates Updating..." />
            </div>
            <p
                v-if="costLines.length === 0 && services.length === 0 && !updatingRates"
                class="grayedout-text text-center"
            >
                No {{ title }} found
            </p>
            <div v-if="!updatingRates && (costLines.length > 0 || services.length > 0)">
                <md-table>
                    <md-table-row slot="header">
                        <md-table-head class="drag-icon"></md-table-head>
                        <md-table-head>Description</md-table-head>
                        <md-table-head>Unit</md-table-head>
                        <md-table-head>Amount</md-table-head>
                        <md-table-head v-if="$root.isDesktop && !isReadOnlyUser && !hasInvoice">Actions</md-table-head>
                    </md-table-row>

                    <div v-if="updatingRates">
                        <loading-component-spinner loading-text="Rates Updating..." />
                    </div>
                    <md-table-row
                        v-if="!updatingRates"
                        v-for="(rate, index) in costLines"
                        :key="rate.id"
                        :class="{
                            'editable-input': editingIndex === index,
                            'readonly-row': editingLoadingIndex === index
                        }"
                    >
                        <rate-charge-rule
                            :rule-data="tripDetails.tripAppliedCostingRule"
                            :currency="tripDetails.currency"
                            :rate="rate"
                            :rate-index="index"
                        />
                        <md-table-cell class="drag-icon">
                            <md-icon v-if="rate.isManualCharge || rate.manuallyChanged">
                                <span class="">drive_file_rename_outline</span>
                            </md-icon>
                        </md-table-cell>
                        <md-table-cell class="description-container">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model="editBuffer.description"
                                        @input="onEditChange"
                                        type="text"
                                        class="md-input editable-value"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                                {{ rate.description }}
                            </template>
                        </md-table-cell>
                        <md-table-cell v-if="rate.type === 'hourly'" class="unitcharge-container">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model="originalEditBuffer.timeInput"
                                        @input="onEditChange"
                                        type="text"
                                        :class="[
                                            'md-input editable-value',
                                            {
                                                'invalid-time-format':
                                                    editingIndex !== null &&
                                                    costLines[editingIndex].type === 'hourly' &&
                                                    originalEditBuffer.timeInput &&
                                                    !$_validateHourlyTimeFormat(originalEditBuffer.timeInput)
                                            }
                                        ]"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                                {{ rate.chargeUnit }}
                            </template>
                        </md-table-cell>
                        <md-table-cell
                            v-else
                            :class="[
                                'unitcharge-container',
                                {
                                    'readonly-fuel-levy':
                                        rate.type === 'fuelLevy' ||
                                        rate.type === 'surcharge' ||
                                        rate.type === 'commission'
                                }
                            ]"
                        >
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model="originalEditBuffer.chargeUnit"
                                        @input="onEditChange"
                                        type="number"
                                        class="md-input editable-value"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                                {{ rate.chargeUnit }}
                            </template>
                        </md-table-cell>
                        <md-table-cell class="amount-container">
                            <template v-if="editingIndex === index">
                                <md-field>
                                    <md-input
                                        v-model.number="editBuffer.amount"
                                        @input="onEditChange"
                                        type="number"
                                        class="md-input editable-value"
                                    />
                                </md-field>
                            </template>
                            <template v-else>
                                {{ $_getRateAmount(rate) | currency(getCurrency) }}
                            </template>
                        </md-table-cell>
                        <md-table-cell class="action-buttons">
                            <template v-if="editingIndex === index">
                                <template v-if="editingLoadingIndex === index">
                                    <md-progress-spinner md-mode="indeterminate" :md-diameter="23" />
                                </template>
                                <template v-else>
                                    <md-button
                                        :disabled="disableSaveButton()"
                                        class="md-success md-just-icon md-round"
                                        @click="saveEdit"
                                    >
                                        <md-icon>save</md-icon>
                                    </md-button>
                                    <md-button class="md-danger md-just-icon md-round" @click="cancelEdit">
                                        <md-icon>cancel</md-icon>
                                    </md-button>
                                </template>
                            </template>
                            <template v-else>
                                <md-button
                                    v-if="
                                        (!isCostsApproved && !hasInvoice && !isReadOnlyUser && !rate.isService) ||
                                            (hasInvoice &&
                                                invoiceStatus() === invoiceStatusAddNewLines &&
                                                rate.type === 'draftCharge')
                                    "
                                    title="Edit Cost"
                                    class="md-success md-just-icon md-round"
                                    @click.stop="startEdit(index, rate)"
                                >
                                    <md-icon>edit</md-icon>
                                </md-button>
                                <md-button
                                    v-if="
                                        (!isCostsApproved && !hasInvoice && !isReadOnlyUser && !rate.isService) ||
                                            (hasInvoice &&
                                                invoiceStatus() === invoiceStatusAddNewLines &&
                                                rate.type === 'draftCharge')
                                    "
                                    title="Remove"
                                    class="md-danger md-just-icon md-round"
                                    @click.stop="deleteCostLine(rate)"
                                >
                                    <md-icon>delete</md-icon>
                                </md-button>
                            </template>
                        </md-table-cell>
                    </md-table-row>
                    <!-- Service cost rows -->
                    <md-table-row v-if="!updatingRates" v-for="svc in mappedServiceCosts" :key="svc.id">
                        <md-table-cell class="drag-icon">
                            <md-icon v-if="svc.isManualCharge || svc.manuallyChanged">
                                <span class="">drive_file_rename_outline</span>
                            </md-icon>
                        </md-table-cell>
                        <md-table-cell>{{ svc.name }} {{ svc.description }}</md-table-cell>
                        <md-table-cell />
                        <md-table-cell>
                            {{ $_getRateAmount(svc) | currency(tripDetails.currency) }}
                        </md-table-cell>
                        <md-table-cell class="action-buttons" />
                    </md-table-row>

                    <md-table-row v-if="!updatingRates">
                        <md-table-cell colspan="3" />
                        <md-table-cell class="total-style">
                            {{ getTotalAmount(costLines) | currency(tripDetails.currency) }}
                        </md-table-cell>
                        <md-table-cell />
                    </md-table-row>
                </md-table>
            </div>
        </md-card-content>
        <md-card-actions
            v-if="$root.isDesktop && !isCostsApproved && !isReadOnlyUser && !hasInvoice"
            :class="{ 'is-loading-rates': updatingRates }"
        >
            <md-button class="md-warning revert-button" @click="validateRevertingCosts()">
                Revert to Default Costs
            </md-button>
            <md-button class="md-success" @click="handleAddNewCost()">Add Cost</md-button>
        </md-card-actions>
        <md-card-actions v-else-if="invoiceStatus() === invoiceStatusAddNewLines">
            <add-new-line-to-draft-invoice
                class="add-line"
                :invoice-id="getInvoiceId()"
                title="Cost"
                :trip-id="tripDetails ? tripDetails.tripId : null"
                @lineCreated="addNewCostLine"
            />
        </md-card-actions>
    </md-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { GeneralMixin, RatesEngineMixin } from '@/mixins';
import { FeatureManager } from '@/directives';
import { FEATURE_NAMES } from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';
import { LoadingComponentSpinner, AddNewLineToDraftInvoice, RateChargeRule } from '@/components';
import TripCostItemModal from './TripCostItemModal';

export default {
    name: 'TripCostRatesList',
    components: {
        LoadingComponentSpinner,
        AddNewLineToDraftInvoice,
        RateChargeRule
    },
    mixins: [GeneralMixin, RatesEngineMixin, FeatureManager],
    computed: {
        ...mapGetters({
            isReadOnlyUser: 'user/isReadOnlyUser',
            isIndividualUser: 'user/isIndividualUser',
            user: 'user/user'
        }),
        mappedServiceCosts() {
            if (!this.services || this.services.length === 0) 
                return [];
            return this.services
                .filter((s) => s.unitCostExTax != null && s.unitCostExTax !== 0 && s.quantity != null)
                .map((s) => ({
                    id: `svc-cost-${s.serviceId}`,
                    name: s.name,
                    description: s.description,
                    quantity: s.quantity,
                    unitPriceExTax: s.unitCostExTax ?? 0,
                    isService: true
                }));
        },
        getCurrency() {
            return this.shipmentDetails?.currency ?? this.stopDetails?.currency;
        }
    },
    props: {
        costLines: {
            type: Array,
            default: () => []
        },
        tripDetails: {
            type: Object,
            default: () => null
        },
        title: {
            type: String,
            default: null
        },
        hasInvoice: {
            type: Boolean,
            default: false
        },
        ratesHaveChanged: {
            type: Boolean,
            default: false
        },
        services: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            updatingRates: false,
            isCostsApproved: false,
            invoiceStatusAddNewLines: 'Draft',
            editingIndex: null,
            editingLoadingIndex: null,
            editBuffer: {
                description: '',
                amount: 0,
                chargeUnit: null,
                timeInput: null
            },
            originalEditBuffer: {
                description: '',
                amount: 0,
                chargeUnit: null,
                timeInput: null
            },
            showEditActions: false,
            originalChargeUnit: null,
            trimmedUnit: null,
            validationTimeout: null
        };
    },
    watch: {
        ratesHaveChanged(newValue, oldValue) {
            this.updatingRates = newValue;
        }
    },
    mounted() {
        if (this.$_hasFeature(FEATURE_NAMES.JobsCostApproval)) {
            this.isCostsApproved = this.tripDetails.isCostsApproved;
        }
    },
    beforeDestroy() {
        // Clean up validation timeout when component is destroyed
        if (this.validationTimeout) {
            clearTimeout(this.validationTimeout);
            this.validationTimeout = null;
        }
    },
    methods: {
        deleteCostLine(cost) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Delete Cost',
                    body: 'Are you sure you want to delete this cost?',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        this.isListLoading = true;
                        await this.handleDeleteCost(cost.id);
                    }
                });
        },
        async handleDeleteCost(id) {
            const payload = {
                method: 'delete'
            };

            this.$_handleLoaderState(true, 'DELETING...');

            const api = `/api/trips/rate/cost/delete/${id}`;
            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Cost was deleted!',
                    type: 'success'
                });
            } catch (e) {
                const message = 'Error in deleting a cost.';
                showErrorMessage(this, message, e);
            } finally {
                this.$_handleLoaderState(false);
                this.emitUpdate();
            }
        },
        handleAddNewCost() {
            this.$modal
                .show(TripCostItemModal, {
                    tripId: this.tripDetails.tripId,
                    isManualCharge: true,
                    manuallyChanged: false,
                    costLine: {
                        description: null,
                        amount: 0,
                        chargeUnit: null,
                        quantity: 1
                    }
                })
                .then((response) => {
                    this.emitUpdate();
                    this.$modal.hide();
                });

            this.postSave();
        },
        validateRevertingCosts() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Revert Costs',
                    body: 'Are you sure you want to revert costs? <br /><br /> This cannot be undone.',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        await this.handleRevertToDefaultCosts();
                    }
                });
        },
        async handleRevertToDefaultCosts() {
            try {
                this.$_handleLoaderState(true, 'REVERTING...');

                const tripData = {
                    isCosting: true,
                    isCharging: false,
                    revertCosts: true,
                    revertCharges: false
                };

                const payload = {
                    method: 'post',
                    data: tripData
                };

                await handleRequests(`/api/rates/trip/${this.tripDetails.tripId}/generate-rates`, payload);

                this.$notify({
                    message: 'Costs Reverted',
                    type: 'success'
                });
            } catch (error) {
                const message = 'Error Reverting Costs';
                showErrorMessage(this, message, null);
            } finally {
                this.postSave();
                this.ratesIsLoading = false;
                this.$_handleLoaderState(false);
                this.emitUpdate();
            }
        },
        emitUpdate() {
            if (this.tripDetails.tripId) {
                this.$emit('updateTripDetails');
            }
        },
        getInvoiceId() {
            if (this.tripDetails && this.tripDetails.teamMemberInvoiceId != null) {
                return this.tripDetails.teamMemberInvoiceId;
            }

            return null;
        },
        invoiceStatus() {
            if (this.tripDetails && this.tripDetails.teamMemberInvoiceStatus != null) {
                return this.tripDetails.teamMemberInvoiceStatus;
            }

            return null;
        },
        addNewCostLine(costLine) {
            const mappedCostLine = {
                id: costLine.id,
                description: costLine.description,
                quantity: costLine.quantity,
                unitPriceExTax: costLine.unitPriceExTax,
                isManualCharge: costLine.isManualCharge,
                manuallyChanged: costLine.manuallyChanged,
                type: costLine.type
            };

            this.costLines.push(mappedCostLine);
            this.postSave();
        },
        startEdit(index, rate) {
            this.$_startEdit(index, rate, this.editBuffer, this.originalEditBuffer);
        },
        onEditChange() {
            this.showEditActions = true;

            // Clear any existing timeout
            if (this.validationTimeout) {
                clearTimeout(this.validationTimeout);
            }

            // Validate hourly time format if editing an hourly rate with debounce
            if (this.editingIndex !== null) {
                const rate = this.costLines[this.editingIndex];
                if (rate.type === 'hourly' && this.originalEditBuffer.timeInput) {
                    this.validationTimeout = setTimeout(() => {
                        const isValid = this.$_validateHourlyTimeFormat(this.originalEditBuffer.timeInput);
                        if (!isValid) {
                            this.$notify({
                                message: 'Invalid time format. Please use format like "4h 54m" or "2h 30m"',
                                type: 'warning'
                            });
                        }
                    }, 1000);
                }
            }
        },
        disableSaveButton() {
            if (!this.showEditActions) {
                return false;
            }

            // Additional validation for hourly rates
            if (this.editingIndex !== null) {
                const rate = this.costLines[this.editingIndex];
                if (rate.type === 'hourly' && this.originalEditBuffer.timeInput) {
                    const canSave = this.$_validateHourlyTimeFormat(this.originalEditBuffer.timeInput);
                    return !canSave;
                }
            }

            return false;
        },
        postSave() {
            this.editingIndex = null;
            this.editingLoadingIndex = null;
            this.showEditActions = false;
        },
        async saveEdit() {
            const result = this.$_saveEdit(
                this.costLines,
                this.editBuffer,
                this.originalEditBuffer,
                'Trip',
                this.tripDetails.tripId,
                'Cost',
                this.isManualCharge
            );

            try {
                if (result && result.api) {
                    await handleRequests(result.api, result.payload);
                    this.$notify({
                        message: `${result.entityType} updated!`,
                        type: 'success'
                    });
                }
            } catch (e) {
                const message = `Cannot edit ${result.entityType.toLowerCase()}.`;
                showErrorMessage(this, message, e);
            } finally {
                this.postSave();
                this.emitUpdate();
            }
        },
        cancelEdit() {
            // Clear validation timeout when cancelling edit
            if (this.validationTimeout) {
                clearTimeout(this.validationTimeout);
                this.validationTimeout = null;
            }

            this.editingIndex = null;
            this.showEditActions = false;
        },
        getTotalAmount() {
            let total = 0;
            this.costLines.forEach((line) => {
                total += Math.round((line.quantity * line.unitPriceExTax + Number.EPSILON) * 100) / 100;
            });
            this.mappedServiceCosts.forEach((line) => {
                total += Math.round((line.quantity * line.unitPriceExTax + Number.EPSILON) * 100) / 100;
            });
            return total;
        }
    }
};
</script>

<style lang="scss" scoped>
.total-style {
    color: #4caf50 !important;
    font-weight: bold !important;
}

.icon-style {
    color: white;
}

.revert-button {
    margin-inline-end: auto !important;
}

.disabled-rates {
    pointer-events: none;
}

.is-loading-rates {
    margin-top: 150px !important;
}

.action-buttons {
    button,
    ::v-deep button {
        margin: 0 2px;
    }

    button:last-child {
        margin-right: 0;
    }
}

::v-deep .md-table-head-label {
    color: #4caf50 !important;
}

.has-invoice,
.is-approved {
    padding-top: 15px;
    padding-left: 20px;
}

.invoice-icon-color {
    color: #4caf50 !important;
}

.invalid-time-format {
    background-color: #ffebee !important;
}

.add-line {
    pointer-events: auto !important;
}

.edit-actions-bar {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 16px;
}

.md-table-cell .md-field {
    margin: 0;
    padding: 0;
}

.md-table-cell .md-input,
.md-table-cell input {
    width: 100%;
    font-size: inherit;
    line-height: inherit;
    padding: 0;
    margin: 0;
    background: transparent;
    border: none;
    box-shadow: none;
    min-width: 0;
    height: 25px;
    box-sizing: border-box;
}

.md-field {
    &::before,
    &::after {
        bottom: 0px;
        display: none;
    }
}

.editable-input {
    background: #f0f0f0 !important;
    border-radius: 4px;
    padding: 10px 15px;
    transition: border 0.2s;
}

.editable-input:focus {
    outline: none;
}

.editable-value {
    border-bottom: 2px solid #e0e0e0 !important;
}

.amount-container {
    width: 150px !important;
}

.description-container {
    width: 300px !important;
}

.row-updating {
    background: #f0f0f0 !important;
}

.edit-spinner {
    display: inline-block;
    vertical-align: middle;
    margin: 0 8px;
}

.unitcharge-container {
    width: 150px;
}

.readonly-row {
    pointer-events: none;
    opacity: 0.6;
}

.readonly-fuel-levy {
    pointer-events: none;
    opacity: 0.8;
}
</style>
